import { WidgetTable } from '@/components/WidgetTable';
import type { WidgetTableProps } from '@/components/WidgetTable/types';
import type { UseQueryResult } from '@tanstack/react-query';

type QueryTableProps<T> = {
  query: UseQueryResult<T[]>;
  columns: WidgetTableProps<T>['columns'];
  onRowClick?: (row: T) => void;
  rowClassName?: (row: T) => string;
  emptyState?: string;
  loadingState?: React.ReactNode;
};

export function QueryTable<T>({
  query,
  columns,
  onRowClick,
  rowClassName,
  emptyState = 'No data',
  loadingState,
}: QueryTableProps<T>) {
  if (query.isLoading) {
    return (
      <div className="flex h-[180px] items-center justify-center text-sm text-muted-foreground">
        {loadingState ?? 'Loading…'}
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="flex h-[180px] items-center justify-center text-sm text-destructive">
        {query.error instanceof Error
          ? query.error.message
          : 'Something went wrong'}
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0">
      <WidgetTable<T>
        data={query.data ?? []}
        columns={columns}
        onRowClick={onRowClick}
        rowClassName={rowClassName}
        emptyState={emptyState}
        className="h-full"
      />
    </div>
  );
}
